import { useTranslation } from 'react-i18next';
import { Play as PlayIcon } from '@vgno/icons';

import { useServerTime } from '../../utils/useServerTime';
import { usePaywallState } from '../../utils/usePaywallState';
import {
  isAssetLoginPaywalled,
  isAssetUpcoming,
  isLiveStream,
  isUpcomingLive,
} from '../../../shared/helpers/assetHelpers';
import { Button } from '../../Button/Button';
import { Spinner } from '../../Spinner/Spinner';
import { SubscribeButton } from '../../SubscribeButton/SubscribeButton';
import { LoginButton } from '../../LoginButton/LoginButton';
import { LoginPaywallButton } from '../../LoginPaywallButton/LoginPaywallButton';
import { useViewportType } from '../../utils/useViewportType';

import type { SvpAssetAdditional } from '@vgtv/api-client/lib/svp_asset';
import type { FC } from 'react';

interface Props {
  asset: SvpAssetAdditional;
  onPlayButtonClick?: () => void;
}

// TODO: show an error message / refresh on the button when paywall state fetch failed
export const PaywallSplashButtons: FC<Props> = (props: Props) => {
  const { asset, onPlayButtonClick } = props;
  const { t } = useTranslation();
  const { serverTime } = useServerTime();
  const { isMobile } = useViewportType();
  const buttonSize = isMobile ? 'medium' : 'large';

  const { paywallState } = usePaywallState(asset);

  const isLoginPaywall = isAssetLoginPaywalled(asset);

  if (paywallState.isFetching) {
    return (
      <Button
        component="span"
        size={buttonSize}
        variant="contained"
        rounded
        data-testid="loading-button"
      >
        <Spinner size="small" />
      </Button>
    );
  }

  if (
    (paywallState.hasAccess && isUpcomingLive(asset, serverTime)) ||
    (paywallState.hasAccess &&
      !isLiveStream(asset) &&
      isAssetUpcoming(asset, serverTime))
  ) {
    return null;
  }

  if (!paywallState.isLoggedIn) {
    return (
      <>
        <SubscribeButton size={buttonSize} asset={asset} />
        {isLoginPaywall ? (
          <LoginPaywallButton size={buttonSize} />
        ) : (
          <LoginButton size={buttonSize} />
        )}
      </>
    );
  }

  if (!paywallState.hasAccess && !isLoginPaywall) {
    return <SubscribeButton asset={asset} size={buttonSize} />;
  }

  return (
    <Button
      size={buttonSize}
      variant="contained"
      onClick={onPlayButtonClick}
      isActiveBrandColor
    >
      <PlayIcon />
      {t('Click to watch')!}
    </Button>
  );
};
